import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Address } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@entities/app/address.entity';
import { ListRowComponent } from '../../../@commons/ui-components/list-row/list-row.component';
import { pluralKey } from '../../../@commons/pipes/vw-plural.pipe';
import { currentAppLang } from '../../../@commons/pipes/vw-date.pipe';
import { TranslateService } from '@ngx-translate/core';
import { LocationHomeIconComponent } from '../../../@icons/location-home-icon.component';
import { LocationListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/location-list.response';
import { GardenAddressToastComponent } from './@toasts/garden-address-toast/garden-address-toast.component';
import { ClimateType } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/climate-type.enum';
import { AddressLocationsService } from '../../address-locations.service';

@Component({
  selector: 'vw-user-address-card',
  encapsulation: ViewEncapsulation.None,
  imports: [
    ListRowComponent,
    LocationHomeIconComponent,
    GardenAddressToastComponent,
  ],
  templateUrl: './user-address-card.component.html',
  styleUrl: './user-address-card.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UserAddressCardComponent implements OnInit {
  @Input() address: Address;
  @Output() closeEditAddressToast = new EventEmitter<void>();

  /** `null` tant que les zones ne sont pas chargées : on n'annonce pas « 0 zone ». */
  locations: LocationListResponse[] | null = null;
  isGardenOpen: boolean = false;
  /** `undefined` : pas encore de réponse ; `null` : réponse sans climat. */
  climateType: ClimateType | null | undefined;

  // Zones et climat viennent du service de l'écran profil : la ligne les lit,
  // la fiche du potager les reçoit d'elle au lieu de les relire.
  private readonly addressLocations = inject(AddressLocationsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);

  ngOnInit() {
    if (this.address && this.address.uid) {
      this.addressLocations
        .locations$(this.address.uid)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((locations) => (this.locations = locations));
      this.addressLocations
        .climate$(this.address.uid)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((climateType) => (this.climateType = climateType));
    }
  }

  /**
   * « Océanique tempéré, 4 zones de culture », avec les seules parties
   * chargées : rien ne s'affiche avant la réponse, « Aucun climat renseigné »
   * seulement si le serveur a répondu sans climat.
   */
  get summary(): string {
    return [this.climateLabel, this.zonesLabel].filter(Boolean).join(', ');
  }

  /** Vide tant que le climat n'est pas connu (ou sur un échec). */
  get climateLabel(): string {
    return this.climateType === undefined
      ? ''
      : this.translate.instant('profile.addresses.climate-types.' + (this.climateType ?? 'unknown'));
  }

  /** « 4 zones de culture » ; vide tant que les zones ne sont pas chargées. */
  get zonesLabel(): string {
    if (!this.locations) return '';
    const count = this.locations.length;
    const key = pluralKey('profile.addresses.zones-count', count, currentAppLang(this.translate), (k) =>
      this.translate.instant(k) !== k,
    );
    return this.translate.instant(key, { count });
  }

  /** La commune d'abord : c'est elle qu'on reconnaît dans une liste. */
  get placeLabel(): string {
    const a = this.address;
    return a?.city || a?.line1 || a?.postalCode || '';
  }

  /** La fiche refermée, le profil relit ses adresses (une adresse a pu changer). */
  onCloseGarden() {
    this.isGardenOpen = false;
    this.closeEditAddressToast.emit();
  }

  onOpenGarden() {
    this.isGardenOpen = true;
  }
}
